import { TriangleAlertIcon } from "lucide-react";
import dayjs from "dayjs";
import { HireUnitType } from "@/gql/graphql";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { relocationDepartureDatesQuery } from "@/app/Relocations/GraphQL/relocationDepartureDatesQuery";
import { keepPreviousData } from "@tanstack/react-query";
import { formatDate } from "@/lib/Formatters/formatDate";
import { useTranslations } from "use-intl";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DepartureDatesWarningProps {
  supplierId: string;
  vehicleId?: string;
  departureOfficeId?: string;
  deliveryOfficeId?: string;
  hireUnitType?: HireUnitType;
  availableFromDate?: string;
  availableToDate?: string;
  fixedLatestDepartureDate?: string | null;
  hireUnitsAllowed?: number;
  extraHireUnitsAllowed?: number;
  minimumHire?: number;
}

export function DepartureDatesWarning({
  supplierId,
  vehicleId,
  departureOfficeId,
  deliveryOfficeId,
  hireUnitType,
  availableFromDate,
  availableToDate,
  fixedLatestDepartureDate,
  hireUnitsAllowed,
  extraHireUnitsAllowed,
  minimumHire,
}: DepartureDatesWarningProps) {
  const canQueryDepartureDates =
    !!supplierId &&
    !!vehicleId &&
    !!departureOfficeId &&
    !!deliveryOfficeId &&
    !!availableFromDate &&
    !!availableToDate &&
    !!hireUnitType &&
    hireUnitsAllowed !== undefined &&
    hireUnitsAllowed !== null &&
    extraHireUnitsAllowed !== undefined &&
    extraHireUnitsAllowed !== null &&
    minimumHire !== undefined &&
    minimumHire !== null;

  const { data: departureDatesData } = useGqlQuery(
    relocationDepartureDatesQuery,
    {
      supplierId: supplierId!,
      vehicleId: vehicleId!,
      departureOfficeId: departureOfficeId!,
      deliveryOfficeId: deliveryOfficeId!,
      hireUnitType: hireUnitType!,
      availableFromDate: availableFromDate!,
      availableToDate: availableToDate!,
      fixedLatestDepartureDate,
      hireUnitsAllowed: hireUnitsAllowed!,
      extraHireUnitsAllowed: extraHireUnitsAllowed!,
      minimumHireUnits: minimumHire!,
    },
    {
      enabled: canQueryDepartureDates,
      placeholderData: keepPreviousData,
    },
  );

  const departureDatesCount =
    departureDatesData?.relocationDepartureDates?.departureDates?.length ??
    null;
  const earliestBookableTime =
    departureDatesData?.relocationDepartureDates?.earliestBookableTime;
  const departureDates =
    departureDatesData?.relocationDepartureDates?.departureDates;

  const hasLimitedDepartureDates =
    departureDatesCount !== null && departureDatesCount <= 3;
  const hasNoDepartureDates = departureDatesCount === 0;

  if (!hasLimitedDepartureDates) {
    return null;
  }

  return (
    <div
      className={`col-span-full rounded-md p-4 ${hasNoDepartureDates ? "bg-red-50" : "bg-yellow-50"}`}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <TriangleAlertIcon
            className={`h-5 w-5 ${hasNoDepartureDates ? "text-red-400" : "text-yellow-400"}`}
          />
        </div>
        <div className="ml-3">
          <h3
            className={`text-sm font-medium ${hasNoDepartureDates ? "text-red-800" : "text-yellow-800"}`}
          >
            {departureDatesCount === 0
              ? "No departure dates"
              : "Limited departure dates"}
          </h3>
          <div
            className={`mt-2 text-sm ${hasNoDepartureDates ? "text-red-700" : "text-yellow-700"}`}
          >
            <p>
              {departureDatesCount === 0 ? (
                "This relocation configuration has no available departure dates. "
              ) : (
                <>
                  This relocation configuration only has{" "}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help border-b border-dashed border-current">
                        {departureDatesCount} departure date
                        {departureDatesCount === 1 ? "" : "s"} available
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <ul className="space-y-1">
                        {departureDates?.map((dateData, index) => (
                          <li key={index}>
                            {dayjs(dateData.date).format("MMMM D, YYYY")}
                            {dateData.ranges && dateData.ranges.length > 0 ? (
                              <span className="ml-2 text-gray-400">
                                (
                                {dateData.ranges.map((range, rangeIndex) => (
                                  <span key={rangeIndex}>
                                    {range.start} - {range.end}
                                    {rangeIndex < dateData.ranges.length - 1
                                      ? ", "
                                      : ""}
                                  </span>
                                ))}
                                )
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                  .{" "}
                </>
              )}
              Consider adjusting the availability window, office hours, or other
              constraints to provide customers with more flexibility.
            </p>

            <ul className="mt-3 list-disc space-y-1">
              <MinimumDaysGreaterThanHireUnits
                minimumHire={minimumHire}
                extraHireUnits={extraHireUnitsAllowed}
                hireUnits={hireUnitsAllowed}
              />

              <MinimumDaysPastAvailableTo
                minimumHire={minimumHire}
                availableTo={availableToDate}
                earliestDeparture={earliestBookableTime!}
                hireUnitType={hireUnitType!}
              />

              <LatestDepartureNearAvailableFrom
                availableFromDate={availableFromDate}
                departureDates={departureDates}
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function MinimumDaysGreaterThanHireUnits({
  minimumHire,
  hireUnits,
  extraHireUnits,
}: {
  minimumHire?: number;
  hireUnits?: number;
  extraHireUnits?: number;
}) {
  if (
    minimumHire !== undefined &&
    hireUnits !== undefined &&
    extraHireUnits !== undefined &&
    minimumHire > hireUnits + extraHireUnits
  ) {
    return (
      <li>
        <strong>Minimum hire is greater than the maximum number of days</strong>
      </li>
    );
  }

  return null;
}

function MinimumDaysPastAvailableTo({
  minimumHire,
  earliestDeparture,
  availableTo,
  hireUnitType,
}: {
  minimumHire: number | undefined;
  earliestDeparture: string | undefined;
  availableTo: string | undefined;
  hireUnitType: HireUnitType;
}) {
  if (!(minimumHire !== undefined && earliestDeparture && availableTo)) {
    return null;
  }

  const unitsToAdd =
    hireUnitType === HireUnitType.Day ? minimumHire - 1 : minimumHire;
  const earliestDelivery = dayjs(earliestDeparture).add(unitsToAdd, "days");
  if (
    dayjs(earliestDeparture).add(unitsToAdd, "days").isAfter(availableTo, "day")
  ) {
    return (
      <li>
        <p>
          <strong>
            The earliest delivery is {formatDate(earliestDelivery)} which is
            after the &apos;Available to&apos; of {formatDate(availableTo)}
          </strong>
        </p>

        <ul className="ml-3 list-inside list-disc">
          <li>Lower the minimum days OR</li>
          <li>Extend the available to</li>
        </ul>
      </li>
    );
  }

  return null;
}

function LatestDepartureNearAvailableFrom({
  availableFromDate,
  departureDates,
}: {
  availableFromDate?: string;
  departureDates?: { date: string }[];
}) {
  const t = useTranslations("relocation.departureDatesWarning");

  if (!availableFromDate || !departureDates || departureDates.length === 0) {
    return null;
  }

  const latestDepartureDate = departureDates.reduce((latest, current) =>
    dayjs(current.date).isAfter(dayjs(latest.date)) ? current : latest,
  );

  const daysDiff = dayjs(latestDepartureDate.date).diff(
    dayjs(availableFromDate),
    "day",
  );

  if (daysDiff <= 3) {
    return (
      <li>
        <strong>
          {t("latestDepartureNearAvailableFrom", {
            latestDate: formatDate(latestDepartureDate.date),
            days: daysDiff,
            availableFrom: formatDate(availableFromDate),
          })}
        </strong>
      </li>
    );
  }

  return null;
}
